/* eslint-disable @next/next/no-img-element */
import { observer } from "mobx-react"
import parse from "react-html-parser"
import { useStore } from "@features/common"
import { CareersPerson } from "@serializers/careers-person"

// Where each quote sits, not what it says or who is pictured. Both of those come
// from the people list in Strapi and are paired with these by position, so entry 1
// here goes with the first person an editor entered.
//
// There is no bundled portrait any more: a quote with no upload renders without
// one.
//
// fallbackName and fallbackQuote belong to the older flat template keys and are
// only reached through the legacy path below. They go away with those keys.
const PEOPLE_LAYOUTS = [
  {
    key: "1",
    alt: "Guy Illustration",
    fallbackName: "mirto",
    fallbackQuote:
      "What I value most at Pure is the culture of trust and flexibility. I feel empowered without micromanagement, and my personal time is respected as much as my work.",
  },
  {
    key: "2",
    alt: "Chantal Illustration",
    fallbackName: "Chantal",
    fallbackQuote:
      "What I love about Pure is that the spirit of the product is reflected in the culture. Just like Pure gives people freedom to connect authentically, working here gives me the flexibility to shape my day while being part of a STAR team that’s bold, creative, and fun.",
  },
  {
    key: "3",
    alt: "Batman Illustration",
    fallbackName: "Grigorii",
    fallbackQuote:
      "At Pure, I feel part of a team that truly supports each other. It’s not just work - it’s a place where people care and grow together.",
  },
  {
    key: "4",
    alt: "Leopard Illustration",
    fallbackName: "Stas",
    fallbackQuote:
      "What Pure pulled off with the company trip is some unreachable level of professionalism, warmth, and attention to people. It makes you feel like you matter to the company, and that’s incredibly motivating. I’ve never had a team - or a chance to get to know a team - like this. Now I’ll spend another whole year telling my friends what a company event can actually be like.",
  },
  {
    key: "5",
    alt: "Amber Illustration",
    fallbackName: "amber",
    fallbackQuote:
      "The internal culture is so supportive and healthy, giving room for friendships in addition to professional relationships. It makes having meetings and collaborating all the more fun. However, the most important thing is that everyone is very respectful of your work, time and self. :)",
  },
]

// Every quote shows its picture the same way: only when Strapi holds one, since
// nothing is bundled with the frontend any more. The frame around it is bespoke
// per quote, so the class list stays with the quote.
type PersonImageProps = {
  person: { image: string; alt: string }
  className: string
}

const PersonImage = (props: PersonImageProps) => {
  const { person, className } = props

  if (!person.image) return null

  return <img src={person.image} alt={person.alt} className={className} />
}

const OurPeople = () => {
  const {
    VacanciesModel: { getTemplate, people: strapiPeople },
  } = useStore()

  const sectionTitle = parse(
    getTemplate("careers.people.title", false) ||
      "Straight from<br/>Our People",
  )

  // Each person takes their words from the Strapi entry that carries their key,
  // and from the older flat template keys for anything that entry leaves empty,
  // so the page keeps working while the content moves across. Matching on the key
  // rather than the position means reordering the list in the admin does not hand
  // a quote somebody else's slot.
  const people = PEOPLE_LAYOUTS.map((layout) => {
    const content: CareersPerson | undefined = strapiPeople.find(
      (person) => person.key === layout.key,
    )

    return {
      image: content?.image ?? "",
      alt: layout.alt,
      name:
        content?.name ||
        getTemplate(`careers.people.${layout.key}.name`) ||
        layout.fallbackName,
      quote:
        content?.quote ||
        getTemplate(`careers.people.${layout.key}.description`) ||
        layout.fallbackQuote,
    }
  })

  return (
    <section className="relative mt-[100px] mb-[50px] tablet:mb-[150px]">
      <div className="w-full max-w-screen-desktop-l mx-auto px-[36px] desktop:pl-[140px] desktop:pr-[120px]">
        <h2 className="w-fit rotate-[-6.45deg] mb-[50px] text-left font-quirk-bold text-[32px] tablet:text-[64px] leading-[40px] tablet:leading-[70px] tracking-[1px] tablet:tracking-[4px] uppercase">
          {sectionTitle}
        </h2>{" "}
      </div>

      <div className="w-full max-w-screen-desktop-l mx-auto px-[36px] desktop:pl-[140px] desktop:pr-[120px] flex flex-col items-center">
        <div className="relative laptop:mb-[100px] flex flex-col laptop:flex-row laptop:justify-center">
          <div className="relative laptop:top-[150px] mb-[30px] w-full laptop:w-fit flex flex-col laptop:flex-row items-center">
            <PersonImage
              person={people[0]}
              className="mb-[10px] w-auto h-auto max-h-[150px] tablet:max-h-[230px]"
            />
            <div className="w-full relative laptop:w-[358px] laptop:shrink-0 laptop:top-[130px] laptop:left-[50px]">
              <p className="w-fit mb-[10px] tablet:mb-[40px] text-[14px] tablet:text-[24px] font-sans-bold tracking-[0.5px] uppercase rotate-[-1deg]">
                {people[0].name}
              </p>
              <p className="rotate-[1deg] tablet:rotate-[4.5deg] w-fit text-[16px] tablet:text-[20px] font-sans leading-[20px] tablet:leading-[28px] ">
                {people[0].quote}
              </p>
            </div>
          </div>

          <div className="mb-[32px] w-full laptop:w-fit relative flex flex-col laptop:flex-row-reverse items-center">
            <PersonImage
              person={people[1]}
              className="relative laptop:top-[-150px] mb-[10px] tablet:mb-0 w-auto h-auto max-h-[170px] tablet:max-h-[294px]"
            />
            <div className="relative laptop:right-[-50px] w-full mt-[-20px] tablet:mt-0 laptop:w-[410px] laptop:shrink-0">
              <p className="w-fit mb-[10px] tablet:mb-[30px] text-[14px] tablet:text-[24px] font-sans-bold tracking-[0.5px] uppercase rotate-[2deg]">
                {people[1].name}
              </p>
              <p className="rotate-[-2deg] w-fit text-[16px] tablet:text-[20px] font-sans leading-[20px] tablet:leading-[28px] ">
                {people[1].quote}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse laptop:flex-row laptop:justify-center gap-[20px] laptop:gap-[100px] laptop:mb-[50px] laptop:w-full">
          <div className="relative laptop:top-[200px] mb-[30px] w-full laptop:w-fit flex flex-col items-center laptop:items-start">
            <PersonImage
              person={people[2]}
              className="mb-[10px] inline-block w-auto h-auto max-h-[150px] tablet:max-h-[234px] tablet:mb-[50px]"
            />
            <div className="w-full relative laptop:max-w-[418px]">
              <p className="w-fit mb-[10px] tablet:mb-[30px] text-[14px] tablet:text-[24px] font-sans-bold tracking-[0.5px] uppercase rotate-[-1deg]">
                {people[2].name}
              </p>
              <p className="rotate-[1deg] tablet:rotate-[2deg] w-fit text-[16px] tablet:text-[20px] font-sans leading-[20px] tablet:leading-[28px]">
                {people[2].quote}
              </p>
            </div>
          </div>
          <div className="mb-[30px] w-full laptop:w-fit relative flex flex-col items-center laptop:items-end">
            <PersonImage
              person={people[3]}
              className="w-auto h-auto max-h-[178px] tablet:max-h-[270px]"
            />
            <div className="w-full laptop:max-w-[561px]">
              <p className="w-fit mb-[10px] tablet:mb-[40px] text-[14px] tablet:text-[24px] font-sans-bold tracking-[0.5px] uppercase rotate-[-1deg]">
                {people[3].name}
              </p>
              <p className="rotate-[2deg] tablet:rotate-[-2deg] w-fit text-[16px] tablet:text-[20px] font-sans leading-[20px] tablet:leading-[28px]">
                {people[3].quote}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full relative flex flex-col laptop:justify-center items-center laptop:flex-row-reverse">
          <PersonImage
            person={people[4]}
            className="mb-[10px] w-auto h-auto max-h-[200px] tablet:max-h-[334px] tablet:mb-0"
          />
          <div className="w-full relative laptop:top-[190px] laptop:right-[-110px] mt-[-20px] laptop:mt-0 laptop:max-w-[530px]">
            <p className="w-fit mb-[10px] tablet:mb-[30px] text-[14px] tablet:text-[24px] font-sans-bold tracking-[0.5px] uppercase rotate-[-1deg]">
              {people[4].name}
            </p>
            <p className="w-fit text-[16px] tablet:text-[20px] font-sans leading-[20px] tablet:leading-[28px] rotate-[-2deg]">
              {people[4].quote}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

const OurPeopleComponent = observer(OurPeople)

export { OurPeopleComponent }
